// src/pages/StudentDashboard.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom'; // +++ ADDED useLocation +++
import { studentTutorialAPI, tutorialAttemptAPI } from '../services/api';

// --- BRAND PALETTE (Matched to Dashboard.js) ---
const BRAND = {
    teal: '#75B9BE',
    cream: '#FDF6EC',
    charcoal: '#2D4042',
    coral: '#F28482',
    white: '#FFFFFF',
    grey: '#F0F2F5',
    danger: '#E07A5F',
    warning: '#F2CC8F'
};

// --- INTERNAL CHART COMPONENT ---
const SimpleBarChart = ({ data, color = BRAND.teal, title }) => {
    const height = 150;
    const width = 500;
    const padding = 30;
    
    if (!data || data.length === 0) {
        return <div style={{height, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize:'0.9em', fontStyle: 'italic', fontWeight: '600'}}>No Data Available</div>;
    }

    const maxVal = Math.max(...data.map(d => d.value), 1);
    
    return (
        <div style={{ 
            background: '#F8F9FA', 
            borderRadius: '12px', 
            padding: '20px', 
            border: '2px solid #E0E0E0' 
        }}>
            <h5 style={{ margin: '0 0 15px 0', color: BRAND.charcoal, fontSize: '0.9em', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '800', fontFamily: 'Nunito, sans-serif' }}>{title}</h5>
            <svg width="100%" viewBox={`0 0 ${width} ${height}`}>
                {[0, 0.5, 1].map(t => {
                    const y = height - padding - (t * (height - 2 * padding));
                    return (
                        <g key={t}>
                            <line x1={padding} y1={y} x2={width - padding} y2={y} stroke="#E0E0E0" strokeDasharray="4" strokeWidth="2" />
                        </g>
                    );
                })}
                {data.map((d, i) => {
                    const barHeight = (d.value / maxVal) * (height - 2 * padding);
                    const barWidth = Math.min((width - 2 * padding) / data.length * 0.6, 40); 
                    const x = padding + (i * ((width - 2 * padding) / data.length)) + ((width - 2 * padding) / data.length - barWidth) / 2;
                    const y = height - padding - barHeight;
                    return (
                        <g key={i}>
                            <rect x={x} y={y} width={barWidth} height={barHeight} fill={color} rx="4" stroke={BRAND.charcoal} strokeWidth="2" />
                            <text x={x + barWidth/2} y={height - padding + 20} textAnchor="middle" fontSize="11" fill={BRAND.charcoal} fontFamily="Inter, sans-serif" fontWeight="600">
                                {d.label}
                            </text>
                             <text x={x + barWidth/2} y={y - 10} textAnchor="middle" fontSize="11" fill={BRAND.charcoal} fontWeight="800" fontFamily="Inter, sans-serif">
                                {d.value}
                            </text>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

// +++ UPDATED: Accepts onLogout prop +++
const StudentDashboard = ({ onLogout }) => {
    const [tutorials, setTutorials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const location = useLocation(); // +++ ADDED +++

    // Filter States
    const [filterName, setFilterName] = useState('');
    const [filterClass, setFilterClass] = useState('');
    const [filterSubject, setFilterSubject] = useState('');
    const [filterLessonPlan, setFilterLessonPlan] = useState('');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');

    // Selected Student History State
    const [selectedStudentData, setSelectedStudentData] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);

    useEffect(() => {
        const fetchTutorials = async () => {
            try {
                const res = await studentTutorialAPI.getAll();
                const completed = res.data.filter(t => t.status === 'COMPLETED');
                setTutorials(completed);
            } catch (err) {
                console.error("Error fetching tutorials:", err);
                setError('Failed to load student tutorials.');
            } finally {
                setLoading(false);
            }
        };
        fetchTutorials();
    }, []);

    const handlePanelClick = async (studentId, studentName) => {
        try {
            setHistoryLoading(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            const res = await tutorialAttemptAPI.getStudentHistory(studentId);
            setSelectedStudentData({
                name: studentName,
                history: res.data
            });
        } catch (err) {
            console.error("Failed to load history", err);
        } finally {
            setHistoryLoading(false);
        }
    };

    // +++ NEW EFFECT: Check for incoming state +++
    useEffect(() => {
        if (location.state?.openStudentId && location.state?.openStudentName) {
            handlePanelClick(location.state.openStudentId, location.state.openStudentName);
            // Clear state to avoid reopening on refresh (optional, but good practice)
            window.history.replaceState({}, document.title);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]); // Only run if location state changes

    // Process History Data
    const getProcessedHistory = () => {
        if (!selectedStudentData || !selectedStudentData.history) return null;

        const accData = [];
        const glossData = [];
        const metaData = [];
        const engData = [];

        selectedStudentData.history.forEach(h => {
            const label = new Date(h.completedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'numeric' });
            const hAnswers = h.answers || [];
            
            // 1. MCQ Accuracy
            const hMcqs = hAnswers.filter(a => !a.questionText.startsWith('Match:'));
            const hCorrect = hMcqs.filter(a => a.isCorrect).length;
            const hAcc = hMcqs.length > 0 ? Math.round((hCorrect / hMcqs.length) * 100) : 0;
            accData.push({ label, value: hAcc });

            // 2. Glossary Attempts
            const hGloss = hAnswers.filter(a => a.questionText.startsWith('Match:'));
            let totalTries = 0;
            hGloss.forEach(a => {
                const tm = a.studentAnswer.match(/Matched in (\d+) tr/);
                totalTries += tm ? parseInt(tm[1]) : 0;
            });
            const avgTries = hGloss.length > 0 ? parseFloat((totalTries / hGloss.length).toFixed(1)) : 0;
            glossData.push({ label, value: avgTries });

            // 3. Mock Metrics
            const seed = new Date(h.completedAt).getDate();
            metaData.push({ label, value: 70 + (seed % 30) });
            engData.push({ label, value: 65 + ((seed * 2) % 35) });
        });

        return { accData, glossData, metaData, engData };
    };


    const processedCharts = getProcessedHistory();

    const getValue = (t, field) => {
        if (field === 'student') return t.studentId?.name || 'Unknown Student';
        if (field === 'class') return t.homeworkId?.homeworkScheduleId?.name || 'Unknown Class';
        if (field === 'subject') return t.homeworkId?.homeworkScheduleId?.subject || 'General';
        if (field === 'lessonPlan') return t.homeworkId?.lessonPlanId?.title || 'Unknown Plan';
        return '';
    };

    const getOptionsFor = useCallback((targetField) => {
        const uniqueValues = new Set();
        tutorials.forEach(t => {
            const matchesName = targetField === 'student' || filterName === '' || getValue(t, 'student') === filterName;
            const matchesClass = targetField === 'class' || filterClass === '' || getValue(t, 'class') === filterClass;
            const matchesSubject = targetField === 'subject' || filterSubject === '' || getValue(t, 'subject') === filterSubject;
            const matchesPlan = targetField === 'lessonPlan' || filterLessonPlan === '' || getValue(t, 'lessonPlan') === filterLessonPlan;
            
            if (matchesName && matchesClass && matchesSubject && matchesPlan) {
                uniqueValues.add(getValue(t, targetField));
            }
        });
        return Array.from(uniqueValues).sort();
    }, [tutorials, filterName, filterClass, filterSubject, filterLessonPlan]);

    const studentOptions = useMemo(() => getOptionsFor('student'), [getOptionsFor]); 
    const classOptions = useMemo(() => getOptionsFor('class'), [getOptionsFor]);
    const subjectOptions = useMemo(() => getOptionsFor('subject'), [getOptionsFor]);
    const lessonPlanOptions = useMemo(() => getOptionsFor('lessonPlan'), [getOptionsFor]);

    const filteredTutorials = tutorials.filter(t => {
        const completedDateObj = new Date(t.completedAt || t.updatedAt);
        const completedDateStr = completedDateObj.toISOString().split('T')[0];

        const matchesName = filterName === '' || getValue(t, 'student') === filterName;
        const matchesClass = filterClass === '' || getValue(t, 'class') === filterClass;
        const matchesSubject = filterSubject === '' || getValue(t, 'subject') === filterSubject;
        const matchesPlan = filterLessonPlan === '' || getValue(t, 'lessonPlan') === filterLessonPlan;

        let matchesDate = true;
        if (filterStartDate) matchesDate = matchesDate && (completedDateStr >= filterStartDate);
        if (filterEndDate) matchesDate = matchesDate && (completedDateStr <= filterEndDate);

        return matchesName && matchesClass && matchesSubject && matchesPlan && matchesDate;
    });

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day:'numeric', month:'short' }) + ' ' + date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    };

    if (loading) {
        return (
            <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'Nunito, sans-serif', color: BRAND.charcoal, background: BRAND.cream, minHeight: '100vh', fontSize: '1.2rem', fontWeight: '700' }}>
                Loading student insights... 🎓
            </div>
        );
    }
    
    if (error) return <div style={{ padding: '20px', color: BRAND.danger, fontWeight: 'bold' }}>{error}</div>;

    return (
        <div style={{ 
            fontFamily: "'Inter', sans-serif",
            backgroundColor: BRAND.cream,
            minHeight: '100vh',
            color: BRAND.charcoal,
            position: 'relative', 
            overflow: 'hidden'    
        }}>
            
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Nunito:wght@700;800&display=swap');
                    
                    h1, h2, h3, h4 { font-family: 'Nunito', sans-serif; }

                    /* BACKGROUND PATTERN */
                    .pattern-bg {
                        background-image: radial-gradient(${BRAND.teal} 15%, transparent 16%), radial-gradient(${BRAND.coral} 15%, transparent 16%);
                        background-size: 60px 60px;
                        background-position: 0 0, 30px 30px;
                        opacity: 0.1;
                        position: absolute;
                        top: 0; left: 0; right: 0; bottom: 0;
                        z-index: 0;
                        pointer-events: none;
                        mask-image: linear-gradient(to bottom, black 0%, transparent 100%);
                        -webkit-mask-image: linear-gradient(to bottom, black 0%, transparent 100%);
                    }

                    .dash-card {
                        background: ${BRAND.white};
                        border-radius: 16px;
                        border: 2px solid ${BRAND.charcoal};
                        box-shadow: 4px 4px 0px rgba(45, 64, 66, 0.1);
                        transition: transform 0.2s ease, box-shadow 0.2s ease;
                        overflow: hidden;
                    }
                    .dash-card:hover {
                        transform: translateY(-2px);
                        box-shadow: 6px 6px 0px rgba(45, 64, 66, 0.15);
                    }
                    
                    .dash-input {
                        padding: 10px 14px;
                        border-radius: 10px;
                        border: 2px solid #E0E0E0;
                        background-color: #F8F9FA;
                        box-sizing: border-box;
                        transition: all 0.2s;
                        outline: none;
                        font-family: 'Inter', sans-serif;
                        font-weight: 500;
                        font-size: 0.9rem;
                        color: ${BRAND.charcoal};
                        min-width: 130px;
                    }
                    .dash-input:focus {
                        background-color: white;
                        border-color: ${BRAND.teal};
                    }

                    /* BASE BUTTON STYLE (Soft Pop) */
                    .btn-base {
                        border: none;
                        border-radius: 10px;
                        padding: 10px 20px;
                        font-family: 'Nunito', sans-serif;
                        font-weight: 800;
                        cursor: pointer;
                        transition: all 0.1s ease;
                        font-size: 0.95rem;
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        border-bottom: 4px solid rgba(0,0,0,0.1); 
                        text-decoration: none;
                    }
                    .btn-base:active {
                        transform: translateY(2px);
                        border-bottom: 2px solid rgba(0,0,0,0.1);
                        margin-bottom: 2px;
                    }

                    .btn-teal { background-color: ${BRAND.teal}; color: white; }
                    .btn-coral { background-color: ${BRAND.coral}; color: white; }
                    /* +++ LOGOUT BUTTON STYLE (White) +++ */
                    .btn-white { 
                        background-color: white; 
                        color: ${BRAND.charcoal}; 
                        border: 2px solid ${BRAND.charcoal}; 
                        border-bottom: 4px solid ${BRAND.charcoal}; 
                        font-size: 0.85rem;
                        padding: 6px 16px;
                    }
                    .btn-white:active {
                        border-bottom: 2px solid ${BRAND.charcoal};
                    }

                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}
            </style>

            <div className="pattern-bg"></div>

            <div style={{ position: 'relative', zIndex: 1, padding: '40px 20px', maxWidth: '1200px', margin: '0 auto' }}>

                {/* +++ UPDATED HEADER +++ */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '40px' }}>
                    <div>
                        <h1 style={{ margin: '0 0 5px 0', color: BRAND.charcoal, fontWeight: '800', fontSize: '2.4em', letterSpacing: '-0.5px' }}>Student Dashboard</h1>
                        <p style={{ margin: 0, color: '#5D7375', fontSize: '1.1rem', fontWeight: '500' }}>Track progress and analyze performance</p>
                    </div>
                    
                    {/* +++ BUTTON GROUP: Stacked & Right Aligned +++ */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px' }}>
                        <button onClick={onLogout} className="btn-base btn-white">
                            Log Out
                        </button>
                        <Link to="/dashboard" style={{ textDecoration: 'none' }}>
                            <button className="btn-base btn-teal">
                                ← Back to Hub
                            </button>
                        </Link>
                    </div>
                </div>

                {/* HISTORY CHART OVERLAY SECTION */}
                {selectedStudentData && (
                    <div className="dash-card" style={{ 
                        padding: '30px', 
                        marginBottom: '40px',
                        position: 'relative',
                        animation: 'fadeIn 0.4s ease-out',
                        border: `2px solid ${BRAND.teal}`
                    }}>
                        <button 
                            onClick={() => setSelectedStudentData(null)}
                            style={{ 
                                position: 'absolute', top: '20px', right: '20px', 
                                background: BRAND.coral, border: 'none', 
                                width: '32px', height: '32px', borderRadius: '50%',
                                boxShadow: '2px 2px 0px rgba(0,0,0,0.2)', cursor: 'pointer', color: 'white',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2em', fontWeight: 'bold'
                            }}
                        >
                            &times;
                        </button>
                        
                        <h2 style={{ marginTop: 0, color: BRAND.charcoal, paddingBottom: '15px', fontSize: '1.6em', borderBottom: '2px dashed #E0E0E0' }}>
                            Performance History: <span style={{color: BRAND.teal}}>{selectedStudentData.name}</span>
                        </h2>

                        {historyLoading ? (
                            <p style={{color: '#888', fontStyle: 'italic'}}>Loading detailed history...</p>
                        ) : processedCharts ? (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '25px', marginTop: '20px' }}>
                                <SimpleBarChart data={processedCharts.accData} title="MCQ Accuracy (%)" color={BRAND.teal} />
                                <SimpleBarChart data={processedCharts.glossData} title="Avg Attempts / Term" color={BRAND.coral} />
                                <SimpleBarChart data={processedCharts.metaData} title="Metacognition Score" color={BRAND.warning} />
                                <SimpleBarChart data={processedCharts.engData} title="Engagement Score" color={BRAND.teal} />
                            </div>
                        ) : (
                            <p>No history data found.</p>
                        )}
                    </div>
                )}

                {/* FILTERS BAR */}
                <div className="dash-card" style={{ 
                    padding: '20px', 
                    marginBottom: '30px',
                    display: 'flex',
                    gap: '15px', 
                    alignItems: 'center',
                    flexWrap: 'wrap',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: '300px', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: '800', color: BRAND.charcoal, marginRight: '10px', fontSize: '0.9em', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Filter</span>
                        
                        <select value={filterName} onChange={(e) => setFilterName(e.target.value)} className="dash-input">
                            <option value="">Student</option>
                            {studentOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>

                        <select value={filterClass} onChange={(e) => setFilterClass(e.target.value)} className="dash-input">
                            <option value="">Class</option>
                            {classOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>

                        <select value={filterSubject} onChange={(e) => setFilterSubject(e.target.value)} className="dash-input">
                            <option value="">Subject</option>
                            {subjectOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>

                        <select value={filterLessonPlan} onChange={(e) => setFilterLessonPlan(e.target.value)} className="dash-input">
                            <option value="">Lesson Plan</option>
                            {lessonPlanOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#F0F2F5', padding: '5px 15px', borderRadius: '12px', border: '1px solid #E0E0E0' }}>
                        <span style={{ fontSize: '0.85em', color: BRAND.charcoal, fontWeight: '700' }}>RANGE</span>
                        <input type="date" value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)} style={{ border: 'none', background: 'transparent', color: BRAND.charcoal, fontFamily: 'Inter', fontWeight: '500' }} />
                        <span style={{ color: BRAND.charcoal }}>-</span>
                        <input type="date" value={filterEndDate} onChange={(e) => setFilterEndDate(e.target.value)} style={{ border: 'none', background: 'transparent', color: BRAND.charcoal, fontFamily: 'Inter', fontWeight: '500' }} />
                    </div>

                    <button 
                        onClick={() => { setFilterName(''); setFilterClass(''); setFilterSubject(''); setFilterLessonPlan(''); setFilterStartDate(''); setFilterEndDate(''); }}
                        className="btn-base"
                        style={{ 
                            background: 'transparent', color: BRAND.charcoal, 
                            border: '2px solid #E0E0E0', borderBottom: '2px solid #E0E0E0', fontSize: '0.9em' 
                        }}
                    >
                        Clear
                    </button>
                </div>

                <div style={{ marginBottom: '20px', fontStyle: 'italic', color: '#5D7375', fontSize: '0.95em', fontWeight: '600' }}>
                    Showing {filteredTutorials.length} completed tutorials
                </div>

                {/* CARDS GRID */}
                {filteredTutorials.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#888', border: '3px dashed #CCD', borderRadius: '16px', background: 'rgba(255,255,255,0.6)' }}>
                        <h3 style={{ fontSize: '1.5rem', fontWeight: '800', color: BRAND.charcoal }}>No tutorials found.</h3>
                        <p>Try adjusting your filters or waiting for students to complete tasks.</p>
                    </div>
                ) : (
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
                        gap: '25px', 
                        rowGap: '30px'
                    }}>
                        {filteredTutorials.map(tutorial => {
                            const studentName = getValue(tutorial, 'student');
                            const lessonPlanTitle = getValue(tutorial, 'lessonPlan');
                            const subject = getValue(tutorial, 'subject');
                            const className = getValue(tutorial, 'class');
                            const completedDate = tutorial.completedAt || tutorial.updatedAt;
                            const studentId = tutorial.studentId?._id || tutorial.studentId;
                            
                            const isActive = selectedStudentData && selectedStudentData.name === studentName;

                            return (
                                <div 
                                    key={tutorial._id} 
                                    onClick={() => handlePanelClick(studentId, studentName)}
                                    className="dash-card"
                                    style={{ 
                                        padding: '25px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        minHeight: '220px',
                                        cursor: 'pointer',
                                        border: isActive ? `3px solid ${BRAND.teal}` : `2px solid ${BRAND.charcoal}`
                                    }}
                                >
                                    <div>
                                        {/* Subject Badge */}
                                        <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                            <span style={{ 
                                                background: subject === 'History' ? '#FEF3C7' : '#E0F2F1', 
                                                color: BRAND.charcoal,
                                                border: '1px solid rgba(0,0,0,0.1)',
                                                padding: '4px 10px', borderRadius: '8px', fontSize: '0.75em', fontWeight: '800', textTransform: 'uppercase'
                                            }}>
                                                {subject}
                                            </span>
                                            <span style={{ fontSize: '0.8em', color: '#666', fontWeight: '600' }}>
                                                {formatDate(completedDate).split(' ')[0]}
                                            </span>
                                        </div>

                                        <h3 style={{ margin: '0 0 10px 0', color: BRAND.charcoal, fontSize: '1.3em', fontWeight: '800' }}>{studentName}</h3>
                                        
                                        <div style={{ fontSize: '0.95em', color: '#555', lineHeight: '1.6' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{ color: '#888', fontWeight: '600', fontSize: '0.9em' }}>CLASS</span> 
                                                <strong style={{color: BRAND.charcoal}}>{className}</strong>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{ color: '#888', fontWeight: '600', fontSize: '0.9em' }}>PLAN</span> 
                                                <strong style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px', color: BRAND.charcoal }}>{lessonPlanTitle}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div style={{ marginTop: '20px', borderTop: '2px solid #F0F2F5', paddingTop: '15px' }}>
                                        <button 
                                            className="btn-base btn-teal"
                                            onClick={(e) => {
                                                e.stopPropagation(); 
                                                navigate(`/tutorial-report/${tutorial._id}`);
                                            }}
                                            style={{ width: '100%', justifyContent: 'center' }}
                                        >
                                            View Full Report
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default StudentDashboard;